import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { Heart, ShieldCheck, Calendar, FileText, Users, Pill, Star } from "lucide-react";
import usePageMeta from "@/hooks/usePageMeta";
import { orgJsonLd, websiteJsonLd, SITE_URL } from "@/lib/jsonld";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HERO_IMG = "https://images.unsplash.com/photo-1583329550487-0fa300a4cd1a?crop=entropy&cs=srgb&fm=jpg&w=1400&q=80";
const HANDS_IMG = "https://images.unsplash.com/photo-1604881991720-f91add269bed?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80";
const FAMILY_IMG = "https://images.unsplash.com/photo-1658632302217-984d432b4d38?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80";

const FEATURES = [
  { icon: Pill, title: "Medications, organized", body: "Track dosages, schedules, and refills without flipping through scribbled notes." },
  { icon: Calendar, title: "The Hope Planner", body: "A calm calendar built around caregiving — appointments, meds, and important dates in one place." },
  { icon: FileText, title: "Documents that stay found", body: "Upload doctor's notes, insurance, and legal papers. Find them in seconds, not hours." },
  { icon: Users, title: "Family, on the same page", body: "Invite siblings, spouses, or care professionals. Everyone sees what matters." },
  { icon: ShieldCheck, title: "Permissions that respect privacy", body: "Owner, contributor, viewer, or professional access — you decide who sees what." },
  { icon: Heart, title: "Built with care, for caregivers", body: "Not clinical. Not cold. A warm digital binder that sits beside you." },
];

export default function Landing() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [testimonials, setTestimonials] = useState([]);

  usePageMeta({
    title: "Hope Chest — A digital binder for family caregivers",
    description: "Organize medications, appointments, contacts, documents, and daily care notes for the people you love. Built by a caregiver, free for one loved one.",
    canonical: SITE_URL,
    jsonLd: [orgJsonLd(), websiteJsonLd()],
  });

  useEffect(() => {
    axios.get(`${API}/public/testimonials`).then((r) => setTestimonials(r.data || [])).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#FCFAF8] text-[#3D3935] relative overflow-hidden">
      {/* Decorative blobs */}
      <div className="hc-blob" style={{ background: "#D8E2D8", width: 500, height: 500, top: -150, right: -100 }} />
      <div className="hc-blob" style={{ background: "#E8DCC8", width: 400, height: 400, top: 600, left: -100 }} />

      {/* Nav */}
      <nav className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
          <img src="/hope-chest-logo.png" alt="Hope Chest" className="w-10 h-10 object-contain" />
          <span className="font-brand text-xl font-semibold tracking-tight">Hope Chest</span>
        </Link>
        <div className="hidden md:flex items-center gap-8 text-sm">
          <a href="#features" className="text-[#7A736E] hover:text-[#3D3935] transition">Features</a>
          <Link to="/pricing" className="text-[#7A736E] hover:text-[#3D3935] transition" data-testid="nav-pricing">Pricing</Link>
          <a href="#philosophy" className="text-[#7A736E] hover:text-[#3D3935] transition">Our philosophy</a>
        </div>
        <div className="flex items-center gap-3">
          {user ? (
            <Button onClick={() => nav("/dashboard")} className="rounded-full bg-[#3A5A4A] hover:bg-[#2C4538]" data-testid="nav-dashboard">Open dashboard</Button>
          ) : (
            <>
              <Link to="/login" className="text-sm text-[#3D3935] hover:text-[#3A5A4A]" data-testid="nav-login">Sign in</Link>
              <Button onClick={() => nav("/signup")} className="rounded-full bg-[#3A5A4A] hover:bg-[#2C4538] px-6" data-testid="nav-signup">Get started</Button>
            </>
          )}
        </div>
      </nav>

      {/* Hero */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-12 md:pt-24 pb-20">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 fade-up">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#D8E2D8] text-[#3A5A4A] text-xs tracking-[0.2em] uppercase font-medium">
              <span className="w-1.5 h-1.5 rounded-full bg-[#3A5A4A]" /> For families caring for someone they love
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold mt-6 leading-[1.05] tracking-tight">
              Every detail of their care.
              <br />
              <span className="text-[#3A5A4A]">In one calm, gentle place.</span>
            </h1>
            <p className="mt-6 text-lg text-[#7A736E] max-w-xl leading-relaxed">
              Hope Chest replaces the shoebox of papers, the missed appointments, and the late-night searching.
              It's a warm digital binder that helps caregivers feel prepared, organized, and less alone.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Button
                size="lg"
                onClick={() => nav(user ? "/dashboard" : "/signup")}
                className="rounded-full bg-[#3A5A4A] hover:bg-[#2C4538] text-white px-8 py-6 text-base"
                data-testid="hero-cta-primary"
              >
                Begin your Hope Chest
              </Button>
              <Button
                variant="ghost"
                size="lg"
                onClick={() => nav("/pricing")}
                className="rounded-full text-[#3D3935] hover:bg-[#F3EFEA] px-8 py-6 text-base"
                data-testid="hero-cta-secondary"
              >
                See what's included →
              </Button>
            </div>
            <p className="mt-6 text-sm text-[#7A736E]">Free forever for your first Hope Chest. No credit card required.</p>
            <p className="mt-2 text-xs text-[#7A736E]" data-testid="hero-auth-note">
              Sign up with Google or email — <span className="text-[#3A5A4A]">both work equally well.</span>
            </p>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative rounded-3xl overflow-hidden shadow-[0_30px_80px_rgba(61,57,53,0.12)] border border-[#EBE5DF]">
              <img src={HERO_IMG} alt="Sunlit calm kitchen" className="w-full h-[420px] md:h-[520px] object-cover" />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl border border-[#EBE5DF] p-4 shadow-lg max-w-[230px] hidden md:block">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#D8E2D8] flex items-center justify-center">
                  <Pill className="w-4 h-4 text-[#3A5A4A]" />
                </div>
                <div>
                  <p className="text-xs text-[#7A736E]">Today, 8:00 AM</p>
                  <p className="text-sm font-medium">Mom's morning meds</p>
                </div>
              </div>
            </div>
            <div className="absolute -top-4 -right-2 bg-white rounded-2xl border border-[#EBE5DF] p-4 shadow-lg max-w-[210px] hidden md:block">
              <p className="text-xs uppercase tracking-wider text-[#7A736E]">Hope Planner</p>
              <p className="text-sm font-medium mt-1">Dr. Chen — Cardiology</p>
              <p className="text-xs text-[#C87B67] mt-1">Tomorrow • 2:30 PM</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-20">
        <div className="max-w-2xl mb-16">
          <p className="text-xs uppercase tracking-[0.2em] text-[#7A736E] mb-4">Everything in one place</p>
          <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight">
            A gentler way to hold all of it.
          </h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {FEATURES.map((f, i) => (
            <div key={i} className="hc-card hc-card-interactive p-7" data-testid={`feature-card-${i}`}>
              <div className="w-11 h-11 rounded-2xl bg-[#D8E2D8] flex items-center justify-center mb-5">
                <f.icon className="w-5 h-5 text-[#3A5A4A]" />
              </div>
              <h3 className="font-display text-lg font-semibold mb-2">{f.title}</h3>
              <p className="text-sm text-[#7A736E] leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Philosophy / Testimonial-style */}
      <section id="philosophy" className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="rounded-3xl overflow-hidden border border-[#EBE5DF]">
            <img src={HANDS_IMG} alt="Two hands resting" className="w-full h-[460px] object-cover" />
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#7A736E] mb-4">Our philosophy</p>
            <blockquote className="font-display text-2xl md:text-3xl leading-relaxed text-[#3D3935]">
              "The goal is not perfection. The goal is helping caregivers feel
              <span className="text-[#3A5A4A]"> more prepared</span>,
              <span className="text-[#3A5A4A]"> more organized</span>, and
              <span className="text-[#3A5A4A]"> less alone</span>."
            </blockquote>
            <p className="mt-8 text-[#7A736E] leading-relaxed">
              Caregiving is a slow, sacred act. We're not here to optimize you — we're here to sit beside you
              at the kitchen table and help you take the next small, organized step.
            </p>
          </div>
        </div>
      </section>

      {/* Testimonials wall — only render if we have approved testimonials */}
      {testimonials.length > 0 && (
        <section className="relative z-10 max-w-6xl mx-auto px-6 md:px-12 py-16" data-testid="testimonials-section">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-[0.2em] text-[#7A736E] mb-3">Caregivers, in their own words</p>
            <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight">
              You're not the only one walking this road.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {testimonials.slice(0, 6).map((t) => (
              <figure key={t.id} className="hc-card p-6 bg-gradient-to-br from-white to-[#FCFAF8]" data-testid={`testimonial-${t.id}`}>
                <div className="flex gap-0.5 mb-3 text-[#C87B67]">
                  {Array.from({ length: t.rating || 5 }).map((_, i) => (
                    <Star key={i} className="w-4 h-4" fill="currentColor" />
                  ))}
                </div>
                <blockquote className="text-[#3D3935] text-sm leading-relaxed mb-4">"{t.message}"</blockquote>
                <figcaption className="text-xs text-[#7A736E]">— {t.user_name}</figcaption>
              </figure>
            ))}
          </div>
        </section>
      )}

      {/* Closing CTA */}
      <section className="relative z-10 max-w-5xl mx-auto px-6 md:px-12 py-20">
        <div className="relative rounded-3xl overflow-hidden">
          <img src={FAMILY_IMG} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#3A5A4A]/80" />
          <div className="relative p-12 md:p-16 text-white">
            <h2 className="font-display text-3xl md:text-4xl font-semibold">Start your first Hope Chest today.</h2>
            <p className="mt-4 text-white/85 max-w-xl">Free forever for one loved one. No setup pressure — add what you have, add more when you're ready.</p>
            <Button
              onClick={() => nav(user ? "/dashboard" : "/signup")}
              className="mt-8 rounded-full bg-white text-[#3A5A4A] hover:bg-[#F3EFEA] px-8 py-6"
              data-testid="closing-cta-button"
            >
              Create your free Hope Chest
            </Button>
          </div>
        </div>
      </section>

      <footer className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-12 border-t border-[#EBE5DF] mt-10 text-sm text-[#7A736E]">
        <div className="flex flex-col md:flex-row justify-between gap-4">
          <span>© {new Date().getFullYear()} Organized Hope. Made with care.</span>
          <div className="flex gap-6">
            <Link to="/privacy" className="hover:text-[#3D3935]" data-testid="footer-privacy">Privacy</Link>
            <Link to="/terms" className="hover:text-[#3D3935]" data-testid="footer-terms">Terms</Link>
            <Link to="/support" className="hover:text-[#3D3935]" data-testid="footer-support">Support</Link>
            <Link to="/resources" className="hover:text-[#3D3935]" data-testid="footer-resources">Resources</Link>
            <Link to="/changelog" className="hover:text-[#3D3935]" data-testid="footer-changelog">What&apos;s new</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
